import { motion } from "framer-motion";
import { useEffect, useRef, useState } from "react";
import { FaPause, FaPlay, FaRedo, FaStepForward } from "react-icons/fa";
import { TiTick } from "react-icons/ti";
import GlassButton from './ui/GlassButton';

const ZC1DemoSection = () => {
  const [isRunning, setIsRunning] = useState(false);
  const [currentPhase, setCurrentPhase] = useState(0);
  const [completedPhases, setCompletedPhases] = useState([]);
  const [validators, setValidators] = useState([
    { id: 'V1', stake: 32000, status: 'idle', voted: false },
    { id: 'V2', stake: 28000, status: 'idle', voted: false },
    { id: 'V3', stake: 35000, status: 'idle', voted: false },
    { id: 'V4', stake: 30000, status: 'idle', voted: false },
    { id: 'V5', stake: 33000, status: 'idle', voted: false },
    { id: 'V6', stake: 27000, status: 'idle', voted: false },
  ]);
  const [blockHeight, setBlockHeight] = useState(1847293);
  const [zkProofs, setZkProofs] = useState({ generated: 15, verified: 14 });
  const [fusakaMetrics, setFusakaMetrics] = useState({
    gasLimit: 150000000,
    peerDAS: 96.7,
    blobs: 1247,
    eof: 94.2
  });
  
  const intervalRef = useRef(null);

  const phases = [
    { name: "Stake-Weighted Selection", desc: "VRF + Stake chooses leader", icon: "🎯" },
    { name: "Block Proposal", desc: "Create new block", icon: "📦" },
    { name: "Data Validation", desc: "Check data availability", icon: "🔍" },
    { name: "Consensus Vote", desc: "2/3 majority needed", icon: "✅" }
  ];

  const runPhase = (phase) => {
    setCurrentPhase(phase);
    
    if (phase === 0) {
      // Select proposer
      const proposerIdx = Math.floor(Math.random() * validators.length);
      setValidators(prev => prev.map((v, i) => ({
        ...v,
        status: i === proposerIdx ? 'proposer' : 'idle',
        voted: false
      })));
    } else if (phase === 1) {
      // Block proposal - update block height
      setBlockHeight(prev => prev + 1);
      setZkProofs({
        generated: 15,
        verified: 12 + Math.floor(Math.random() * 4)
      });
    } else if (phase === 2) {
      // Data validation - start voting
      setValidators(prev => prev.map(v => ({
        ...v,
        status: v.status === 'proposer' ? 'proposer' : 'voting'
      })));
    } else if (phase === 3) {
      // Consensus - validators approve
      setValidators(prev => prev.map(v => ({
        ...v,
        status: v.status === 'proposer' ? 'proposer' : 'approved',
        voted: true
      })));
      setCompletedPhases(prev => [...prev, phase]);
      
      // Update Fusaka metrics
      setFusakaMetrics(prev => ({
        gasLimit: 150000000 + Math.floor(Math.random() * 10000000),
        peerDAS: 94.5 + Math.random() * 4,
        blobs: 1200 + Math.floor(Math.random() * 100),
        eof: 93 + Math.random() * 5
      }));
    }
    
    if (phase < 3) {
      setCompletedPhases(prev => [...prev, phase]);
    }
  };

  const startDemo = () => {
    if (isRunning) return;
    setIsRunning(true);
    resetDemo();
    
    let phase = 0;
    runPhase(phase);
    
    intervalRef.current = setInterval(() => {
      phase++;
      if (phase >= 4) {
        // Complete and restart
        setTimeout(() => {
          phase = 0;
          setCompletedPhases([]);
          runPhase(phase);
        }, 1500);
      } else {
        runPhase(phase);
      }
    }, 2000);
  };

  const stopDemo = () => {
    setIsRunning(false);
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  };

  const stepDemo = () => {
    if (isRunning) stopDemo();
    const nextPhase = currentPhase >= 3 ? 0 : currentPhase + 1;
    if (nextPhase === 0) setCompletedPhases([]);
    runPhase(nextPhase);
  };

  const resetDemo = () => {
    stopDemo();
    setCurrentPhase(0);
    setCompletedPhases([]);
    setValidators(prev => prev.map(v => ({
      ...v,
      status: 'idle',
      voted: false
    })));
  };

  useEffect(() => {
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, []);

  const getPhaseClass = (idx) => {
    if (completedPhases.includes(idx)) return "bg-emerald-500/30 border-emerald-500/50";
    if (currentPhase === idx) return "bg-cyan-500/30 border-cyan-500/50 scale-105";
    return "bg-white/5 border-white/10";
  };

  const getValidatorClass = (status) => {
    switch (status) {
      case 'proposer': return "bg-gradient-to-r from-cyan-500/30 to-teal-500/30 border-cyan-400/50";
      case 'voting': return "bg-gradient-to-r from-amber-500/30 to-orange-500/30 border-amber-400/50";
      case 'approved': return "bg-gradient-to-r from-emerald-500/30 to-green-500/30 border-emerald-400/50";
      default: return "bg-white/5 border-white/10";
    }
  };

  return (
    <div className="mb-48 p-8 md:p-16 bg-white/5 border border-white/10 rounded-[4rem] relative overflow-hidden">
      {/* Background decoration */}
      <div className="absolute top-0 right-0 w-96 h-96 bg-cyan-500/5 rounded-full blur-[120px]" />
      <div className="absolute bottom-0 left-0 w-96 h-96 bg-purple-500/5 rounded-full blur-[120px]" />
      
      <div className="relative z-10">
        {/* Header */}
        <div className="text-center mb-16">
          <h3 className="text-3xl md:text-4xl font-bold text-white mb-4 uppercase tracking-widest">
            🔗 ZC-1 <span className="text-cyan-400 italic font-zentry">Live Demo</span>
          </h3>
          <p className="text-gray-300 max-w-2xl mx-auto text-lg">
            Watch the DAG-BFT-PoS consensus in action: stake-weighted selection, block proposals, and Byzantine fault tolerance.
          </p>
        </div>

        {/* Controls */}
        <div className="flex flex-wrap gap-4 justify-center mb-12">
          <GlassButton
            onClick={isRunning ? stopDemo : startDemo}
            variant="cyan"
            size="md"
          >
            {isRunning ? <><FaPause className="mr-2" /> Pause</> : <><FaPlay className="mr-2" /> Start Demo</>}
          </GlassButton>
          <GlassButton
            onClick={stepDemo}
            variant="gray"
            size="md"
          >
            <FaStepForward className="mr-2" /> Step
          </GlassButton>
          <GlassButton
            onClick={resetDemo}
            variant="gray"
            size="md"
          >
            <FaRedo className="mr-2" /> Reset
          </GlassButton>
        </div>

        {/* Phases */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-12">
          {phases.map((phase, idx) => (
            <motion.div
              key={idx}
              animate={{ scale: currentPhase === idx ? 1.05 : 1 }}
              className={`p-6 rounded-2xl border-2 transition-all duration-300 ${getPhaseClass(idx)}`}
            >
              <div className="text-3xl mb-3 text-center">{phase.icon}</div>
              <h4 className="font-bold text-white text-sm text-center mb-2">{idx + 1}. {phase.name}</h4>
              <p className="text-gray-400 text-xs text-center">{phase.desc}</p>
              {completedPhases.includes(idx) && (
                <div className="flex justify-center mt-3">
                  <span className="text-emerald-400 text-lg"><TiTick /></span>
                </div>
              )}
            </motion.div>
          ))}
        </div>

        {/* Main Grid: Validators + Metrics */}
        <div className="grid lg:grid-cols-2 gap-8">
          {/* Validators Panel */}
          <div className="bg-black/40 backdrop-blur-xl border border-white/10 rounded-3xl p-8">
            <h4 className="text-xl font-bold text-cyan-400 mb-6 flex items-center gap-2">
              🏛️ Validators ({validators.length})
            </h4>
            <div className="grid grid-cols-2 gap-3">
              {validators.map((v) => (
                <motion.div
                  key={v.id}
                  animate={{ scale: v.status === 'proposer' ? 1.05 : 1 }}
                  className={`p-4 rounded-xl border transition-all duration-300 ${getValidatorClass(v.status)}`}
                >
                  <div className="flex justify-between items-center">
                    <span className="font-bold text-white">{v.id}</span>
                    <span className="text-xs text-gray-400">{(v.stake / 1000).toFixed(0)}K</span>
                  </div>
                  <div className="text-xs text-gray-500 mt-1 capitalize">{v.status}</div>
                </motion.div>
              ))}
            </div>
            <div className="mt-6 text-xs text-gray-500">
              <span className="inline-block w-3 h-3 rounded bg-cyan-500/50 mr-1"></span> Proposer
              <span className="inline-block w-3 h-3 rounded bg-amber-500/50 mr-1 ml-3"></span> Voting
              <span className="inline-block w-3 h-3 rounded bg-emerald-500/50 mr-1 ml-3"></span> Approved
            </div>
          </div>

          {/* Metrics Panel */}
          <div className="bg-black/40 backdrop-blur-xl border border-white/10 rounded-3xl p-8">
            <h4 className="text-xl font-bold text-purple-400 mb-6 flex items-center gap-2">
              📊 Block & Metrics
            </h4>
            <div className="space-y-4">
              <div className="flex justify-between p-3 bg-white/5 rounded-xl">
                <span className="text-gray-300">Block Height</span>
                <span className="font-mono text-cyan-400 font-bold">{blockHeight.toLocaleString()}</span>
              </div>
              <div className="flex justify-between p-3 bg-white/5 rounded-xl">
                <span className="text-gray-300">zk-SNARK Proofs</span>
                <span className="font-mono text-emerald-400 font-bold">{zkProofs.verified}/{zkProofs.generated}</span>
              </div>
              <div className="flex justify-between p-3 bg-white/5 rounded-xl">
                <span className="text-gray-300">Success Rate</span>
                <span className="font-mono text-emerald-400 font-bold">{((zkProofs.verified / zkProofs.generated) * 100).toFixed(1)}%</span>
              </div>
            </div>

            {/* Fusaka Metrics */}
            <h4 className="text-lg font-bold text-orange-400 mt-8 mb-4 flex items-center gap-2">
              🔥 Fusaka Protocol
            </h4>
            <div className="grid grid-cols-2 gap-3">
              <div className="p-4 bg-white/5 rounded-xl text-center">
                <div className="text-xs text-gray-400 mb-1">Gas Limit</div>
                <div className="text-lg font-bold text-emerald-400">{(fusakaMetrics.gasLimit / 1000000).toFixed(0)}M</div>
              </div>
              <div className="p-4 bg-white/5 rounded-xl text-center">
                <div className="text-xs text-gray-400 mb-1">PeerDAS</div>
                <div className="text-lg font-bold text-emerald-400">{fusakaMetrics.peerDAS.toFixed(1)}%</div>
              </div>
              <div className="p-4 bg-white/5 rounded-xl text-center">
                <div className="text-xs text-gray-400 mb-1">Blobs</div>
                <div className="text-lg font-bold text-emerald-400">{fusakaMetrics.blobs.toLocaleString()}</div>
              </div>
              <div className="p-4 bg-white/5 rounded-xl text-center">
                <div className="text-xs text-gray-400 mb-1">EOF</div>
                <div className="text-lg font-bold text-emerald-400">{fusakaMetrics.eof.toFixed(1)}%</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ZC1DemoSection;
